/* eslint-disable no-nested-ternary */
import React, { memo } from 'react';

// material-ui
import { Box, Button, Checkbox, FormControlLabel, Grid, Input, makeStyles, MenuItem, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';

import { useDispatch, useSelector } from 'react-redux';
import { getSpecificCustomer, selectCustomerById } from 'store/customers/customersSlice';
import { RootState } from 'store';

// project imports
import config from 'config';
import SubCard from 'ui-component/cards/SubCard';
import Logo from 'ui-component/Logo';
import MyChip from 'ui-component/extended/Chip';

import { IconCurrencyShekel } from '@tabler/icons';

// Locale
import { format, parseJSON } from 'date-fns';
import { enUS, he } from 'date-fns/locale';
import { FormattedMessage } from 'react-intl';

// types
import { PayNote } from 'types/DocumentsTypes/PayNote';
import {
    deserializePayment,
    deserializePaymentMethod,
    Payment,
    PaymentMethod,
    paymentMethods
} from 'types/DocumentsTypes/utils';
import { BankAutocomplete } from './BankAutocomplete';
import DocumentHeaderPreview from '../document_header_preview/DocumentHeaderPreview';
import DocumentClientDetails from '../document_header_preview/DocumentClientDetails';
import NoNegative, {WithNegative} from 'ui-component/textFieldNumber';
import HoverableIncrementer from 'ui-component/HoverableIncrementer';

import JSDB from 'fromKotlin/nk';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import PreviewDocumentComments from "../PreviewComments";
import CancelNoteDialog from "../../../../../ui-component/dialogs/cancelNoteDialog";
import {ExtendedDocument, isSupplierDocument, displayLinkedInvoiceIds} from "../../../../../types/DocumentsTypes/Document";
import {cancelPaymentLine, documentPhotoCount, getDocumentPhoto, MAX_DOC_PHOTOS, sendReqForApi} from "../../../../../api/customer/documents.api";
import ShowImageDialog, {FieldPhotoCameraIcon} from "../../../../../ui-component/dialogs/showImageDialog";
import ExternalSoftwareSendChip from '../ExternalSoftwareSendChip';
import {SNACKBAR_OPEN} from "../../../../../store/actions";
import Swal from "sweetalert2";
import {getSpecificSupplier, getSuppliers, selectSupplierById} from "../../../../../store/suppliers/suppliersSlice";
import {callbackNetworkResult} from "../../../../../api/customer/files.api";
import {roundDecimals} from "../../../../../fromKotlin/nkutils";
import {refreshDocs} from "../../../../../hooks/customer/documents.hooks";
import MyDatePicker from 'ui-component/MyDatePicker';



interface ReceiptPreviewProps extends PayNote {
    onPaymentChange?: (payment: Payment) => void;
    creditRecepitValue?: boolean;
    setCreditRecepitValue?: React.Dispatch<React.SetStateAction<boolean>>
    setDocFromBot?: React.Dispatch<React.SetStateAction<ExtendedDocument | undefined>>
    pool?: string;
    outerDoc?: any;
    setInternalEdit?: (edit: any) => void;
    internalEdit?: any;
    onPaymentsChange?: (payments: Payment[]) => void;
}



const ReceiptPreview = ({ onPaymentChange, creditRecepitValue, setCreditRecepitValue, setDocFromBot, pool, outerDoc, setInternalEdit, internalEdit, onPaymentsChange, ...document }: ReceiptPreviewProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const linkedInvoiceIds = displayLinkedInvoiceIds(document);

    // TODO: Change to single customer fetching
    const customer = isSupplierDocument(document.type) ? useSelector((state: RootState) => selectSupplierById(state, document.entId)) : useSelector((state: RootState) => selectCustomerById(state, document.entId));
    React.useEffect(() => {
        if (!customer) {
            if (isSupplierDocument(document.type)) {
                dispatch(getSpecificSupplier(Number(document.entId)));
                dispatch(getSuppliers());
            } else {
                dispatch(getSpecificCustomer(Number(document.entId)));
            }
        }
    }, [dispatch, customer, document.entId, document.type]);

    const refresher = refreshDocs(pool);
    const [sendSuccess, setSendSuccess] = React.useState<boolean>(false);
    const [photoCount, setPhotoCount] = React.useState<number>(documentPhotoCount(document));
    React.useEffect(() => {
        setPhotoCount(documentPhotoCount(document));
    }, [document.id, document.photo, document.photoCount]);
    const address = document.details?.street && document.details?.streetNumber ? `${document.details.street}, ${document.details.streetNumber}` : document.details?.street;
    const apiConfig = JSDB().apiConfig
    const cancelPaymentL = React.useCallback(
        async (id,date,payment:Payment) => {
            try {
                const p = deserializePayment(payment);
                const oData = await cancelPaymentLine(id,date,p.pay_method,payment.amount,p.check_number,p.bank_id, isSupplierDocument(document.type));

                // Keep the opened receipt in sync after canceling a payment line.
                // The API may also create a new pay note, but the current one must be refreshed too.
                let docToShow: ExtendedDocument = oData as ExtendedDocument;
                try {
                    const refreshedDocs = await refresher.mutateAsync({
                        ids: [document.id],
                        types: [document.nkObject.getConnectedDocType()],
                        full: true
                    });
                    const refreshedCurrentDoc = (refreshedDocs as ExtendedDocument[]).find(
                        (d) => Number(d.id) === Number(document.id) && d.type === document.type
                    );
                    if (refreshedCurrentDoc) {
                        docToShow = refreshedCurrentDoc;
                    }
                } catch (refreshError) {
                    console.log(refreshError);
                }

                setDocFromBot?.(docToShow);


                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                    },
                    message: `קבלת זיכוי נוצרה בהצלחה`,
                    variant: 'alert',
                    alertSeverity: 'success'
                });

                return true
            } catch (e) {
                console.log(e);
                Swal.fire({
                    title: 'שגיאה בגישה לנתונים',
                    target: '#custom-target',
                    text: '',
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#d63030'
                });
                return false
            }

        },
        [dispatch, document, refresher, setDocFromBot]
    );

    if (!customer) return <Box />;
    return (
        <SubCard
            darkTitle
            title={
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <Grid container sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <Typography fontWeight="bold">
                            <FormattedMessage id="Receipt" /> {document.id ? document.id : ''}
                        </Typography>
                        {document.id ? (
                            <ShowImageDialog
                                pool={pool}
                                onPhotoCountChange={(n) => {
                                    setPhotoCount(n);
                                    document.photo = n > 0;
                                    document.photoCount = n;
                                }}
                                calcFunction={() => getDocumentPhoto(document)}
                                icon={<FieldPhotoCameraIcon count={photoCount}/>}
                                docs={[document]}
                                title={'צילום בשטח'}
                                tooltip={photoCount > 0 ? `צילום בשטח (${photoCount}/${MAX_DOC_PHOTOS})` : 'אין צילום'}
                                subtitle={photoCount > 0 ? 'צילום בשטח' : 'אין צילום'}
                            />
                        ) : undefined}
                    </Grid>
                    {(document && document.id != 0 && apiConfig && apiConfig.hasOne()) ?
                        <MyChip color={'primary'}
                                onClick={(c) => callbackNetworkResult(() => sendReqForApi(document.id, 7), (result) => {
                                    setSendSuccess(true)
                                    refresher.mutate({
                                        ids: [document.id], types: [document.nkObject.getConnectedDocType()],
                                        full: true,
                                    })
                                })}
                                label={(document.one != undefined && document.one != null && document.one.send || sendSuccess) ? 'נשלח לOne' : 'לא נשלח לOne'}></MyChip> :
                        undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasManager()) ?
                        <MyChip color={'primary'}
                                onClick={(c) => callbackNetworkResult(() => sendReqForApi(document.id, 7), (result) => {
                                    setSendSuccess(true)
                                    refresher.mutate({
                                        ids: [document.id], types: [document.nkObject.getConnectedDocType()],
                                        full: true,
                                    })
                                })}
                                label={(document.manager != undefined && document.manager != null && document.manager.send || sendSuccess) ? 'נשלח למנגר' : 'לא נשלח למנגר'}></MyChip> :
                        undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasStoreNext()) ?
                        <MyChip color={'primary'}
                                onClick={(c) => callbackNetworkResult(() => sendReqForApi(document.id, 7), (result) => {
                                    setSendSuccess(true)
                                    refresher.mutate({
                                        ids: [document.id], types: [document.nkObject.getConnectedDocType()],
                                        full: true,
                                    })
                                })}
                                label={(document.storeNext != undefined && document.storeNext != null && document.storeNext.send || sendSuccess) ? 'נשלח לסטורנקס' : 'לא נשלח סטורנקטס'}></MyChip> :
                        undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasHashavshevet()) ?
                        <MyChip color={'primary'}
                                onClick={(c) => callbackNetworkResult(() => sendReqForApi(document.id, 7), (result) => {
                                    setSendSuccess(true)
                                    refresher.mutate({
                                        ids: [document.id], types: [document.nkObject.getConnectedDocType()],
                                        full: true,
                                    })
                                })}
                                label={(document.hashavshevet != undefined && document.hashavshevet != null && document.hashavshevet.send || sendSuccess) ? 'נשלח לחשבשבת' : 'לא נשלח לחשבשבת'}></MyChip> :
                        undefined
                    }
                    <ExternalSoftwareSendChip
                        document={document}
                        refresher={refresher}
                        docType={7}
                    />
                </div>


            }
            secondary={<Logo width={50} />}
        >
            <Grid container spacing={2}>
                <DocumentHeaderPreview />
                <Grid item xs={12}>
                    <Grid container>
                        <DocumentClientDetails address={address} name={customer.name}
                            businessName={document.details!.companyName}
                            businessId={document.details!.companyId} id={customer.id} external_id={customer.externalId}
                            phone={document.details!.phone} email={document.details!.email} />
                        <Grid item sm={6}>
                            <Grid container>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    <FormattedMessage id="Receipt Details" />
                                </Typography>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={4}>
                                            <Typography variant="body2">
                                                <FormattedMessage id="Date" />:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            {onPaymentsChange ? (
                                                <MyDatePicker
                                                    date={internalEdit?.date ?? document.date}
                                                    autoFocus
                                                    setDate={(date) => date && setInternalEdit && setInternalEdit({
                                                        ...internalEdit,
                                                        date,
                                                        date_edit: 1
                                                    })}
                                                    isLoading={undefined}
                                                />
                                            ) : (
                                                <Typography variant="body2">
                                                    {format(document.date, 'PPP', {
                                                        locale: config.i18n === 'he' ? he : enUS
                                                    })}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant='body2'>
                                                תאריך הנפקה:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant='body2'>
                                                {format(document.issueDate, 'PPPPp', {
                                                    locale: config.i18n === 'he' ? he : enUS
                                                })}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="body2">
                                                <FormattedMessage id="Status" />:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <MyChip
                                                label={<FormattedMessage id={document.status} />}
                                                variant="outlined"
                                                size="small"
                                                chipcolor={
                                                    document.status === 'pending_approval'
                                                        ? 'warning'
                                                        : document.status === 'approved'
                                                            ? 'success'
                                                            : document.status === 'open' || document.status==="partly_closed"
                                                                ? 'error'
                                                                : ''
                                                }
                                            />
                                        </Grid>
                                        <>
                                            <Grid item xs={4}>
                                                <Typography variant='body2'>
                                                    סוכן:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant='body2'>
                                                    {document?.agent}
                                                </Typography>
                                            </Grid>
                                        </>
                                        {linkedInvoiceIds.length > 0 ? (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    <FormattedMessage id="Invoice ID's" />:
                                                </Typography>
                                            </Grid>
                                        ) : undefined}
                                        {linkedInvoiceIds.map((d, i) => (
                                            <Grid key={i} item xs={4}>
                                                <Typography variant="subtitle1">{d}</Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container>
                        <Grid item sm={12}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                <FormattedMessage id="Payment Methods" />
                            </Typography>
                        </Grid>
                        {document.payments.map((p) => (
                            <React.Fragment key={p.id}>
                                ________________________________________________________________________________________________________________________________________________________
                                <Grid item sm={9}>
                                    {!onPaymentChange  ? (
                                        <div style={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-between', }}>
                                            { document.status!='canceled' &&(p.method === 'check'  || p.method === 'fastCheck') && p.canceled == 0 ? <CancelNoteDialog
                                                curDate={document.date.getMonth() != (new Date()).getMonth() ? new Date() : document.date}

                                                text={'להחזיר צ"ק?'}
                                                cancelFunc={(date) => {
                                                    cancelPaymentL(document.id, date, p)
                                                }
                                                }/> : undefined}
                                            <Typography variant="subtitle1">
                                                {paymentMethods[p.method].name}
                                            </Typography>
                                            <Typography variant="subtitle1" style={{ direction: 'ltr' }}>
                                                {p.amount}
                                                <IconCurrencyShekel size={10} color="black" />
                                            </Typography>
                                            <Typography variant="subtitle2" >
                                                <FormattedMessage id="Bank" />: {p?.check?.bank}
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-between', }}>

                                            <WithNegative
                                                id={'negativeinp'}
                                                changer={(v)=>   onPaymentChange({ ...p, amount: Number(v) })}
                                                type={'text'}
                                                value={p.amount}

                                                sx={{ width: '50%', mb: 1, mt: 1 }}
                                            />
                                            <TextField
                                                id='outlined-select-currency'
                                                select
                                                label='בחר/י שיטת תשלום'
                                                sx={{ width: '50%', mb: 1, mt: 1 }}
                                                value={p.method}
                                                onChange={(m) => onPaymentChange({ ...p, method: m.target.value as PaymentMethod })}
                                            >
                                                {Object.keys(paymentMethods).map((p) => (
                                                    <MenuItem key={p} value={p}>
                                                        {paymentMethods[p].name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                            {p.method === 'deposit' || p.method === 'check'  || p.method === 'fastCheck' ?
                                            <div>
                                                <Typography variant="subtitle2">
                                                    <FormattedMessage id="Bank" />
                                                </Typography>
                                                <BankAutocomplete
                                                    value={p.check?.bank}
                                                    size='small'
                                                    label=''
                                                    sx={{
                                                        "& .MuiOutlinedInput-root": {
                                                            borderRadius: "50",
                                                            border: "transparent",
                                                            background: "transparent",
                                                            padding: 1,
                                                            width: 180
                                                        },
                                                        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                                                            border: "transparent",
                                                        }
                                                    }}
                                                    onChange={(bank) => onPaymentChange({ ...p, check: { ...p.check, bank } })}
                                                />
                                            </div> :undefined}
                                        </div>

                                    )}

                                </Grid>
                                {p.check && p.method === 'check' && (

                                    <>
                                        <Grid item sm={4}>
                                            {!onPaymentChange ? (
                                                <Typography variant="subtitle2">
                                                    <FormattedMessage id="Account" />: {p.check.account}
                                                </Typography>
                                            ) : (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        <FormattedMessage id="Account" />:
                                                    </Typography>
                                                    <HoverableIncrementer
                                                        value={p.check.account}
                                                        disabled={false}
                                                        onIncrement={() => {
                                                            {
                                                                ((p.check && p.check.account) || p.check?.account === 0) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, account: +(p.check.account) + 1 }
                                                                    });
                                                            }
                                                        }}
                                                        onDecrement={() => {
                                                            {
                                                                (p.check && p.check.account) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, account: Math.max(0, +(p.check.account) - 1) }
                                                                    });
                                                            }
                                                        }}
                                                        onChange={(v) => {
                                                            onPaymentChange({ ...p, check: { ...p.check, account: (v) } });
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item sm={3}>
                                            {!onPaymentChange ? (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        <FormattedMessage id="Branch" />: {p.check.branch}
                                                    </Typography>
                                                </div>
                                            ) : (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        <FormattedMessage id="Branch" />:
                                                    </Typography>
                                                    <HoverableIncrementer
                                                        value={p.check.branch}
                                                        disabled={false}

                                                        onIncrement={() => {
                                                            {
                                                                ((p.check && p.check.branch) || p.check?.branch === 0) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, branch: +(p.check.branch + 1) }
                                                                    });
                                                            }
                                                        }}
                                                        onDecrement={() => {
                                                            {
                                                                (p.check && p.check.branch) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, branch: Math.max(0, +(p.check?.branch) - 1) }
                                                                    });
                                                            }
                                                        }}
                                                        onChange={(v) => {
                                                            onPaymentChange({ ...p, check: { ...p.check, branch: (v) } });
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Grid>

                                        <Grid item sm={2} />
                                        <Grid item sm={4}>

                                            {!onPaymentChange ? (
                                                <Typography variant="subtitle2">
                                                    <FormattedMessage id="Check No." />: {p.check.id}
                                                </Typography>
                                            ) : (
                                                <div>
                                                    <Typography variant="subtitle2">
                                                        <FormattedMessage id="Check No." />
                                                    </Typography>
                                                    <HoverableIncrementer
                                                        value={p.check.id}
                                                        disabled={false}

                                                        onIncrement={() => {
                                                            {
                                                                ((p.check && p.check.id) || p.check?.id === 0) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, id: +(p.check.id) + 1 }
                                                                    });
                                                            }
                                                        }}
                                                        onDecrement={() => {
                                                            {
                                                                (p.check && p.check.id) &&
                                                                    onPaymentChange({
                                                                        ...p,
                                                                        check: { ...p.check, id: Math.max(0, +(p.check?.id) - 1) }
                                                                    });
                                                            }
                                                        }}
                                                        onChange={(v) => {
                                                            onPaymentChange({ ...p, check: { ...p.check, id: (v) } });
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item sm={7}>
                                            {!onPaymentChange ? (
                                                <Typography variant="subtitle2">
                                                    <FormattedMessage id="Due Date" />:{' '}
                                                    {format(
                                                        p.check?.dueDate
                                                            ? typeof p.check.dueDate === 'object'
                                                                ? p.check.dueDate
                                                                : parseJSON(p.check.dueDate)
                                                            : document.date,
                                                        'PP',
                                                        {
                                                            locale: config.i18n === 'he' ? he : enUS
                                                        }
                                                    )}
                                                </Typography>

                                            ) : (
                                                <div style={{ margin: 6 }} >
                                                    <LocalizationProvider sx={{ mt: 3 }} dateAdapter={AdapterDateFns} locale={config.i18n === 'he' ? he : enUS}>
                                                        <DatePicker

                                                            mask=''
                                                            renderInput={(props) => <TextField {...props} />}
                                                            label={<FormattedMessage id='Due Date' />}
                                                            value={p.check?.dueDate}
                                                            onChange={(newValue) => {
                                                                if (newValue) onPaymentChange({
                                                                    ...p,
                                                                    check: { ...p.check, dueDate: newValue }
                                                                });
                                                            }}
                                                        />
                                                    </LocalizationProvider>
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item sm={1} />
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                        {onPaymentsChange ? (
                            <Grid item xs={12}>
                                <Button
                                    size='small'
                                    onClick={() => onPaymentsChange(document.payments.concat({
                                        id: `p${Date.now()}`,
                                        amount: 0,
                                        method: 'cash',
                                        check: {dueDate: new Date()}
                                    }))}
                                >
                                    הוסף תשלום
                                </Button>
                                <Button
                                    size='small'
                                    color='error'
                                    onClick={() => onPaymentsChange(document.payments.slice(0, -1))}
                                    disabled={document.payments.length === 0}
                                >
                                    הסר תשלום אחרון
                                </Button>
                            </Grid>
                        ) : null}
                    </Grid>
                </Grid>
                {
                    document?.recepitValue - document?.mainValue > 0 ? (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={creditRecepitValue} onChange={(e) => setCreditRecepitValue(e.target.checked)} />
                                }
                                label={`זכה סכום שנותר מהחשבוניות (${roundDecimals(document?.recepitValue - document?.mainValue)} ש"ח) `}
                            />
                        </Grid>
                    ) : undefined
                }
                <Grid item xs={12}>
                    <SubCard
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={6} >
                                <FormattedMessage id="Total" />:
                            </Grid>
                            <Grid item xs={6} style={{ direction: 'ltr' }}>
                                <Typography variant="subtitle1" fontWeight="bold" >
                                    {(onPaymentsChange
                                        ? document.payments.reduce((sum, p) => sum + Number(p.amount || 0), 0)
                                        : document.mainValue
                                    ).toLocaleString()}
                                    <IconCurrencyShekel size={10} color="black" />
                                </Typography>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <PreviewDocumentComments
                                outerDoc={outerDoc}
                                comment={document.comment ?? ''}
                                doc={document as ExtendedDocument}
                                pool={pool}
                                refresher={refresher}
                            />
                        </Grid>
                    </SubCard>
                </Grid>
            </Grid>
        </SubCard >
    );
};

export default memo(ReceiptPreview);


